import { useMemo } from 'react';
import { Search_RequestBody } from '@opensearch-project/opensearch/api';
import { QueryContainer } from '@opensearch-project/opensearch/api/_types/_common.query_dsl';
import { ResponseBody } from '@opensearch-project/opensearch/api/_types/_core.search';

import { useIsServerSide, useSelectedStore } from '@/hooks';
import {
  EnumCustomerType,
  FulfillmentType,
  OpenSearchProduct,
  TreezStore,
} from '@/types';
import { useEcomStoreSelector } from '@/data';
import { getDefaultStore, resolveTimeZone } from '@/utils';
import { ORIGIN_BACKEND_HEADER } from '@/constants';
import { Bucket } from 'opensearch/types';
import { mapProduct } from 'utils/opensearch';

import {
  appendQueryClausesToSearchBody,
  buildCustomerTypeFilter,
  buildInventoryLocationFilter,
  buildQueryFilterFromSearchParams,
  getPriceFilter,
  parseInventoryLocationIds,
} from '../utils';
import { useFulFillmentType } from '@/hooks/useFulFillmentType';

export type SearchResponseBody = ResponseBody & {
  _timeZone?: string;
};

const openSearchApiEndpoint =
  process.env.NEXT_PUBLIC_OPEN_SEARCH_ENDPOINT || '';
const openSearchApiKey =
  process.env.NEXT_PUBLIC_OPEN_SEARCH_API_KEY ||
  'jbS0CPECEy8u1LVZcqgqg4lTYCdy6nJtBkEl4GK2';

class SearchService {
  private _entityId: string | undefined;
  private _orgId: string | undefined;
  private _customerType: EnumCustomerType | undefined;
  private _isStrictMedicalEnabled: boolean | undefined;
  private _inventoryLocationIds: string[];
  private _timeZone: string;
  private _fulfillmentType: FulfillmentType | undefined;

  constructor({
    entityId,
    orgId,
    customerType,
    isStrictMedicalEnabled,
    inventoryLocationIds,
    timeZone,
    fulfillmentType,
  }: {
    entityId?: string;
    orgId?: string;
    customerType?: EnumCustomerType;
    isStrictMedicalEnabled?: boolean;
    inventoryLocationIds?: string[];
    timeZone?: string;
    fulfillmentType?: FulfillmentType;
  }) {
    const defaultStore = getDefaultStore();

    this._entityId = entityId ?? defaultStore?.mso_store_entity_id;
    this._orgId = orgId ?? defaultStore?.mso_org_id;
    this._customerType = customerType;
    this._isStrictMedicalEnabled = isStrictMedicalEnabled;
    this._inventoryLocationIds = inventoryLocationIds ?? [];
    this._timeZone = resolveTimeZone(timeZone);
    this._fulfillmentType = fulfillmentType;
  }

  search = async ({
    searchBody,
    revalidate = 120,
  }: {
    searchBody: Search_RequestBody;
    revalidate?: number;
  }) => {
    const runtimeClauses: QueryContainer[] = [];

    if (
      this._customerType &&
      ![EnumCustomerType.ALL, EnumCustomerType.BOTH].includes(
        this._customerType,
      )
    ) {
      runtimeClauses.push(
        buildCustomerTypeFilter(
          this._customerType,
          this._isStrictMedicalEnabled,
        ),
      );
    }

    if (this._inventoryLocationIds.length) {
      runtimeClauses.push(
        buildInventoryLocationFilter(this._inventoryLocationIds),
      );
    }

    const body = appendQueryClausesToSearchBody(searchBody, runtimeClauses);
    const stringifiedBody = JSON.stringify(body);

    const headers = {
      'Content-Type': 'application/json',
      'entity-id': this._entityId ?? '',
      'org-id': this._orgId ?? '',
      'X-Api-Key': openSearchApiKey ?? '',
      ...(process.env.X_API_ORIGIN_BACKEND
        ? { [ORIGIN_BACKEND_HEADER]: process.env.X_API_ORIGIN_BACKEND }
        : {}),
    };

    const response = await fetch(openSearchApiEndpoint, {
      method: 'POST',
      headers,
      body: stringifiedBody,
      next: { tags: ['opensearch'], revalidate },
      cache: 'force-cache',
    });

    if (!response.ok) {
      // eslint-disable-next-line no-console
      console.error('SearchService search error:', {
        status: response.status,
        statusText: response.statusText,
        json: await response.json(),
      });
      throw new Error('Network response was not ok');
    }

    const parsed = (await response.json()) as SearchResponseBody;

    return {
      ...parsed,
      _timeZone: this._timeZone,
    };
  };

  getProductBySlug = async ({ slug }: { slug: string }) => {
    const searchBody: Search_RequestBody = {
      from: 0,
      size: 1,
      query: {
        term: {
          slug: { value: slug },
        },
      },
    };

    const response = await this.search({ searchBody });

    if (response.hits.hits.length === 0) return undefined;

    const product = mapProduct({
      product: response.hits.hits[0]._source as OpenSearchProduct,
      timeZone: this._timeZone,
      fulfillmentType: this._fulfillmentType,
    });

    const siblings = await this.getProductsByCatalogParentId({
      catalogParentId: product.catalogParentId!,
    });

    return {
      ...product,
      siblings,
    };
  };

  getProductsByCategory = async ({
    category,
    pageSize = 20,
  }: {
    category: string;
    pageSize?: number;
  }) => {
    const searchBody: Search_RequestBody = {
      from: 0,
      size: pageSize,
      query: {
        bool: {
          should: [
            {
              match: {
                'category.keyword': category,
              },
            },
          ],
        },
      },
    };

    const response = await this.search({ searchBody });

    return response.hits.hits.map(hit =>
      mapProduct({
        product: hit._source as OpenSearchProduct,
        timeZone: this._timeZone,
        fulfillmentType: this._fulfillmentType,
      }),
    );
  };

  getProductsByGroupId = async ({
    groupId,
    pageSize = 500,
  }: {
    groupId: string;
    pageSize?: number;
  }) => {
    const searchBody: Search_RequestBody = {
      from: 0,
      size: pageSize,
      query: {
        bool: {
          should: [
            {
              match: {
                customProductGroups: groupId,
              },
            },
          ],
        },
      },
    };

    const response = await this.search({ searchBody });

    return response.hits.hits.map(hit =>
      mapProduct({
        product: hit._source as OpenSearchProduct,
        timeZone: this._timeZone,
        fulfillmentType: this._fulfillmentType,
      }),
    );
  };

  getProductsByCatalogParentId = async ({
    catalogParentId,
  }: {
    catalogParentId: string;
  }) => {
    const searchBody: Search_RequestBody = {
      query: {
        bool: {
          must: [
            {
              bool: {
                should: [
                  {
                    match: {
                      catalogParentId: catalogParentId,
                    },
                  },
                ],
              },
            },
            {
              bool: {
                should: [
                  {
                    match: {
                      isHideFromMenu: false,
                    },
                  },
                ],
              },
            },
            {
              bool: {
                should: [
                  {
                    match: {
                      isAboveThreshold: true,
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    };

    const response = await this.search({ searchBody });

    return response.hits.hits
      .sort((a, b) => (a._source?.amount ?? 0) - (b._source?.amount ?? 0))
      .map(hit =>
        mapProduct({
          product: hit._source as OpenSearchProduct,
          timeZone: this._timeZone,
          fulfillmentType: this._fulfillmentType,
        }),
      );
  };

  getProductsIdsByGroupId = async ({
    groupId,
    pageSize = 300,
  }: {
    groupId: string;
    pageSize?: number;
  }) => {
    const searchBody: Search_RequestBody = {
      _source: false,
      from: 0,
      size: pageSize,
      query: {
        bool: {
          must: [
            {
              bool: {
                should: [
                  {
                    match: {
                      customProductGroups: groupId,
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    };

    const response = await this.search({ searchBody });

    // Sort for a deterministic order: OpenSearch hit order is otherwise
    // non-deterministic across identical refetches, which would change the
    // derived deal-page `id` filter value and make the listing reflow/flicker.
    return response.hits.hits.map(hit => hit._id).sort();
  };

  getProductsByIds = async ({ ids }: { ids: string[] }) => {
    const searchBody: Search_RequestBody = {
      size: ids.length,
      query: {
        bool: {
          should: ids.map(id => ({
            match: { id },
          })),
        },
      },
    };

    const response = await this.search({ searchBody });

    return response.hits.hits.map(hit =>
      mapProduct({
        product: hit._source as OpenSearchProduct,
        timeZone: this._timeZone,
        fulfillmentType: this._fulfillmentType,
      }),
    );
  };

  getFacetItems = async ({ facet }: { facet: string }): Promise<string[]> => {
    const queryFilters = buildQueryFilterFromSearchParams(
      {
        isHideFromMenu: [false],
        isAboveThreshold: [true],
      },
      this._timeZone,
    );

    const searchBody: Search_RequestBody = {
      size: 0,
      aggs: {
        facets: {
          global: {},
          aggs: {
            [facet]: {
              filter: {
                bool: {
                  must: [...Object.values(queryFilters), getPriceFilter({})],
                },
              },
              aggs: {
                values: {
                  terms: {
                    field: facet,
                    size: 200,
                  },
                },
              },
            },
          },
        },
      },
    };

    const response = await this.search({ searchBody });

    return (
      response.aggregations?.facets?.[facet]?.values?.buckets?.map(
        (bucket: Bucket) => String(bucket.key),
      ) ?? []
    );
  };

  getNestedFacets = async ({
    parentField,
    childField,
  }: {
    parentField: string;
    childField: string;
  }): Promise<{
    nestedFacets: Record<string, string[]>;
  }> => {
    const searchBody: Search_RequestBody = {
      size: 0,
      aggs: {
        facets: {
          global: {},
          aggs: {
            nestedFacets: {
              filter: {
                bool: {
                  must: [
                    {
                      match_all: {},
                    },
                  ],
                },
              },
              aggs: {
                parents: {
                  terms: {
                    field: parentField,
                    size: 1000,
                  },
                  aggs: {
                    children: {
                      terms: {
                        field: childField,
                        size: 1000,
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    };

    const response = await this.search({ searchBody });

    type ParentBucket = Bucket & { children?: { buckets?: Bucket[] } };
    type FacetsAgg = {
      nestedFacets?: {
        parents?: { buckets?: ParentBucket[] };
      };
    };
    const aggs = response.aggregations as { facets?: FacetsAgg } | undefined;
    const buckets = aggs?.facets?.nestedFacets?.parents?.buckets ?? [];

    const nestedFacets: Record<string, string[]> = {};
    for (const bucket of buckets) {
      const parent = String(bucket.key);
      const childBuckets = bucket.children?.buckets ?? [];
      nestedFacets[parent] = childBuckets.map((b: Bucket) => String(b.key));
    }

    return { nestedFacets };
  };
}

type Args = {
  selectedStore?: TreezStore;
  selectedCustomerType?: EnumCustomerType;
};

export const useSearchService = (args?: Args) => {
  const { store } = useSelectedStore();
  const { fulfillmentType } = useFulFillmentType();
  const { customer_type, provider_inventory_location_id } =
    useEcomStoreSelector(['customer_type', 'provider_inventory_location_id']);
  const storeToUse = store ?? args?.selectedStore;
  const isStrictMedicalEnabled = storeToUse?.strict_medical_enabled;
  const timeZone = resolveTimeZone(storeToUse?.dispensary_time_zone);
  const { isServerSide } = useIsServerSide();
  const customerTypeToUse = isServerSide
    ? args?.selectedCustomerType
    : customer_type;

  const inventoryLocationIds = useMemo(
    () => parseInventoryLocationIds(provider_inventory_location_id),
    [provider_inventory_location_id],
  );

  const searchService = useMemo(
    () =>
      new SearchService({
        entityId: storeToUse?.mso_store_entity_id,
        orgId: storeToUse?.mso_org_id,
        customerType: customerTypeToUse,
        isStrictMedicalEnabled,
        inventoryLocationIds,
        timeZone,
        fulfillmentType,
      }),
    [
      storeToUse?.mso_store_entity_id,
      storeToUse?.mso_org_id,
      customerTypeToUse,
      isStrictMedicalEnabled,
      inventoryLocationIds,
      timeZone,
      fulfillmentType,
    ],
  );

  return searchService;
};

export { SearchService };
